import { useState, useEffect } from 'react';

// ─── PER-(SYMBOL × TF) PINE VERSION PICKER (reusable) — 2026-07-13 ───────────
// Single source of truth for the symbol-isolated Pine version UI, used by:
//   1. LIVE AUTO TRADING → Edit Bot → Asset Basket (deploy/tag per symbol × TF)
//   2. MAMTA AI PRO → BOTS COMMAND bot cards (load a version into the editor)
//   3. MAMTA AI PRO → PINE EDITOR header (load per bot+symbol)
// God RULE #7 (2026-07-17): a leg is (symbol, TF), so a Pine's identity is too.
// NIFTY_50@1s and NIFTY_50@5s are DIFFERENT legs and may run different Pines —
// pass `timeframe` and this picker shows/tags that leg's Pine. Omit it and the
// component behaves exactly as it did before (per-symbol only).
// Semantics (God rules, 2026-07-13):
//   - Default view = ONLY this symbol's own tagged versions (clean), newest
//     first, header shows "✅ <SYM> · LATEST v<N> · <date>" — LATEST is what
//     the SYMPINE router actually runs.
//   - Older versions stay saved in the history group, marked "🕘 old".
//   - Untagged/other-symbol rows hide behind an explicit "➕ tag" toggle —
//     they are the only path to tag a first/new version onto a symbol.
//   - DEPLOY re-registers the chosen version with symbols:[SYM] so it becomes
//     that symbol's newest tag (router picks it up, zero reader changes).
//   - Optional onLoad turns the picker into a loader too (editor use-cases);
//     loading NEVER tags — tagging stays an explicit DEPLOY.

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'https://api.jaiswalcapital.com';

export const pineVerCache: Record<string, any[]> = {};
// RULE #14 (God 2026-07-25): version lists must not live forever — a tab left
// open all session showed pre-save numbers. 60s TTL; DEPLOY still hard-clears.
const pineVerCacheTs: Record<string, number> = {};
const PV_CACHE_TTL_MS = 60_000;
export const pvNorm = (s: string) =>
  String(s || '').trim().toUpperCase().replace(/&/g, '_').replace(/-/g, '_').replace(/\s+/g, '_');
// `symbols` is a string[] since the 2026-07-10 schema fix. Rows written before
// that may still hold a JSON-stringified array ('["NIFTY 50"]') or a CSV
// string — read all three shapes.
export const pvSymsOf = (v: any): string[] => {
  let raw: any = v?.symbols;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    if (s.startsWith('[')) { try { raw = JSON.parse(s); } catch { /* CSV below */ } }
  }
  const list = Array.isArray(raw) ? raw : String(raw || '').split(/[,\n]/);
  return Array.from(new Set(list.map(pvNorm).filter(Boolean)));
};

// ─── TIMEFRAME IDENTITY — God RULE #7 ────────────────────────────────────────
// MIRROR of bot_scope.norm_tf (Algo-Engine/bot_scope.py) and normTfs
// (server.cjs). ONE normaliser law across the whole stack: '1s', '1_Second' and
// '1sec' are the SAME namespace. Two spellings minting two namespaces is exactly
// how the RULE #9 5x sizing bug stayed invisible for weeks.
// Engine spellings ('5_Second') are included on purpose — the headless router
// normalises the ENGINE tf for legs that carry no UI tf (headless_engine:461).
// Unknown/empty -> '' = "no TF" -> the per-symbol Pine. Never guessed: an
// unknown spelling must collapse to the honest bucket, not invent a namespace
// that looks real but matches no leg.
const PV_TF_ALIASES: Record<string, string> = {
  '1s': '1s', '1_second': '1s', '1sec': '1s', '1second': '1s',
  '5s': '5s', '5_second': '5s', '5sec': '5s', '5second': '5s',
  '15s': '15s', '15_second': '15s',
  '1m': '1m', '1_minute': '1m', '1min': '1m',
  '3m': '3m', '3_minute': '3m',
  '5m': '5m', '5_minute': '5m',
  '15m': '15m', '15_minute': '15m',
  '30m': '30m', '30_minute': '30m',
  '1h': '1h', '1_hour': '1h', '60m': '1h',
  '4h': '4h', '4_hour': '4h',
  '1d': '1d', '1_day': '1d',
};
export const pvNormTf = (tf?: string): string =>
  PV_TF_ALIASES[String(tf || '').trim().toLowerCase()] || '';
/** `timeframes` tags of a version. [] = untagged = the per-symbol Pine (every
    Pine today). Same three legacy shapes as `symbols` — array / JSON string / CSV. */
export const pvTfsOf = (v: any): string[] => {
  let raw: any = v?.timeframes;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    if (s.startsWith('[')) { try { raw = JSON.parse(s); } catch { /* CSV below */ } }
  }
  const list = Array.isArray(raw) ? raw : String(raw || '').split(/[,\n]/);
  return Array.from(new Set(list.map(pvNormTf).filter(Boolean)));
};

export interface PineVersionPickerProps {
  pineId: string;
  symbol: string;
  /** God RULE #7: this leg's timeframe ('1s'/'5s'/…). Set it and the picker
      shows + tags the Pine for (symbol × TF), mirroring the headless router.
      Omit/empty = per-symbol Pine (pre-RULE-#7 behaviour, unchanged). */
  timeframe?: string;
  /** When set, a ⬇ LOAD button appears — loads the chosen version's code
      (or the ACTIVE one from the 👁 modal) WITHOUT tagging anything. */
  onLoad?: (code: string, v: any) => void;
  /** Let the select stretch in wider layouts (bot cards / editor header). */
  grow?: boolean;
}

export function PineVersionPicker({ pineId, symbol, timeframe, onLoad, grow }: PineVersionPickerProps) {
  const [vers, setVers] = useState<any[]>(pineVerCache[pineId] || []);
  const [sel, setSel] = useState<string>('');
  const [busy, setBusy] = useState<'' | 'saving' | 'done' | 'err'>('');
  const [err, setErr] = useState<string>('');
  const [showCode, setShowCode] = useState(false);
  // God 2026-07-13: default view = ONLY this symbol's own versions (clean).
  // Untagged/other-symbol rows stay reachable behind an explicit toggle —
  // they are the only path to tag a first/new version onto this symbol.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    const fresh = pineVerCache[pineId]
      && Date.now() - (pineVerCacheTs[pineId] || 0) < PV_CACHE_TTL_MS;
    if (fresh) { setVers(pineVerCache[pineId]); return; }
    if (pineVerCache[pineId]) setVers(pineVerCache[pineId]);   // stale-while-refetch
    fetch(`${API_BASE_URL}/api/bot/versions/${encodeURIComponent(pineId)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const v = Array.isArray(d?.versions) ? d.versions : [];
        pineVerCache[pineId] = v; pineVerCacheTs[pineId] = Date.now(); setVers(v);
      }).catch(() => {});
  }, [pineId]);
  // ── SSOT ENGINE-TRUTH (RULE #14, God 2026-07-25) ───────────────────────────
  // Ask /scanner/pine-active — the SAME resolver the headless engine uses
  // (parity-proven) — which version THIS (bot,symbol,TF) leg actually runs.
  // The header shows the ENGINE's answer; if the picker's local computation
  // ever disagrees, we say so loudly instead of letting the UI lie.
  const [ssot, setSsot] = useState<any>(null);
  const [ssotNonce, setSsotNonce] = useState(0);
  useEffect(() => {
    let dead = false;
    if (!pineId || !symbol) { setSsot(null); return; }
    fetch(`${API_BASE_URL}/scanner/pine-active?bot=${encodeURIComponent(pineId)}`
      + `&symbol=${encodeURIComponent(pvNorm(symbol))}`
      + `&tf=${encodeURIComponent(pvNormTf(timeframe) || '')}&t=${Date.now()}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!dead && d?.status === 'success') setSsot(d); })
      .catch(() => { /* endpoint unreachable — local computation stands alone */ });
    return () => { dead = true; };
  }, [pineId, symbol, timeframe, ssotNonce]);
  const want = pvNorm(symbol);
  const wantTf = pvNormTf(timeframe);
  const tfSfx = wantTf ? `@${wantTf}` : '';
  // Explicit newest-first — server order par bharosa nahi.
  const live = vers.filter(v => v.status !== 'ARCHIVED')
    .slice().sort((a, b) => (Number(b?.version_num) || 0) - (Number(a?.version_num) || 0));
  const symAll = live.filter(v => pvSymsOf(v).includes(want));
  // ACTIVE resolution = STRICT router semantics. This MUST mirror
  // headless_engine.py (cfg_by_leg -> cfg_by_symbol) and routes_trading.py
  // (_active_pine_version) exactly — a picker that shows one version while the
  // router runs another is worse than no picker at all.
  //   1. {symbol, THIS TF} tagged     <- per-leg Pine       (God RULE #7)
  //   2. {symbol} tagged, NO TF tag   <- per-symbol Pine    (every Pine today)
  //   3. generic (no symbol tag)      <- bot-wide fallback
  // Step 2 EXCLUDES TF-tagged rows deliberately: without that guard a 1s-tagged
  // Pine at a higher version_num would hijack the 5s leg (the guard exists in
  // both backend layers; the UI must not disagree with it).
  const tfTagged = wantTf ? symAll.filter(v => pvTfsOf(v).includes(wantTf)) : [];
  const symOnly = symAll.filter(v => pvTfsOf(v).length === 0);
  const mine = tfTagged.length ? tfTagged : symOnly;
  // TRUE only when a TF was asked for and this leg has no Pine of its own, so it
  // inherits the symbol-level one. In a TF-less context (Pine Editor, bot cards)
  // the symbol-level Pine IS the right answer — never mark it as "inherited".
  const inherited = !!wantTf && !tfTagged.length && mine.length > 0;
  const genericV = live.find(v => pvSymsOf(v).length === 0);
  const activeV = mine[0] || genericV || null;
  const activeSrc = tfTagged.length ? `${want}${tfSfx} PER-TF TAG`
    : symOnly.length ? `${want} SYMBOL-TAG${wantTf ? ' (is TF ka apna Pine nahi)' : ''}`
      : activeV ? 'GENERIC fallback' : 'NO PINE';
  // ENGINE-TRUTH vs local computation (RULE #14): the number Sir must trust is
  // the ENGINE's. A disagreement is shown, never hidden.
  const engineV: number | null = ssot && ssot.version != null ? Number(ssot.version) : null;
  const localV: number | null = activeV?.version_num != null ? Number(activeV.version_num) : null;
  const vMismatch = engineV != null && localV != null && engineV !== localV;
  // Untagged versions must stay selectable: tagging happens by DEPLOYing one of
  // them onto this symbol. Listing only already-tagged rows made the first tag
  // impossible from the UI (the `//@symbol` directive was the sole path in).
  // Rows tagged to this symbol on a DIFFERENT TF land here too — re-taggable,
  // and never silently shown as this leg's active Pine.
  const mineIds = new Set(mine.map(v => String(v._id)));
  const rest = live.filter(v => !mineIds.has(String(v._id)));
  const activeId = String(mine[0]?._id || '');           // newest tagged = what the router picks
  const byId = (id: string) => live.find(x => String(x._id) === id);
  const dt = (v: any) => String(v?.createdAt || '').slice(0, 10) || '—';
  const tfChip = (v: any) => { const t = pvTfsOf(v); return t.length ? `@${t.join(',')}` : ''; };
  const label = (v: any) =>
    `v${v.version_num}${tfChip(v)} · ${String(v.bot_name || v.description || '').slice(0, 18)} · ${dt(v)} · ${v.status || 'DRAFT'}`;
  const codeOf = (v: any) => String(v?.pine_code || v?.code_json || '');
  const deploy = async () => {
    const v = byId(sel);
    if (!v) return;
    setBusy('saving');
    try {
      // Status must be one of the backend enum (DRAFT|PAPER|LIVE|ARCHIVED).
      // This used to send "CONFIRMED", which failed schema validation → 500,
      // so no deploy from this picker ever persisted.
      const status = ['DRAFT', 'PAPER', 'LIVE'].includes(v.status) ? v.status : 'DRAFT';
      const r = await fetch(`${API_BASE_URL}/api/bot/versions/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          bot_id: pineId, name: `${v.bot_name || pineId} [${want}${tfSfx} v${v.version_num}]`,
          pine_code: v.pine_code || v.code_json, code_json: v.code_json || v.pine_code,
          symbols: [want],
          // God RULE #7: omit the field entirely when this leg has no TF — an
          // absent tag means "per-symbol Pine", which is what the router's
          // fallback query looks for. Sending [] would mean the same, but not
          // sending it keeps legacy rows and new rows literally identical.
          ...(wantTf ? { timeframes: [wantTf] } : {}),
          status,
          description: `Asset Basket tag: ${want}${tfSfx} <- v${v.version_num} (${dt(v)})`,
        }),
      });
      const d0 = await r.json().catch(() => ({} as any));
      if (!r.ok || d0.status !== 'ok') throw new Error(d0.message || String(r.status));
      // Trust the server's echo, not our request body — proves the tag landed.
      // This check is not ceremony: `timeframes` was missing from the Mongoose
      // schema until 2026-07-17, and strict mode DROPS unknown fields silently.
      // Without reading the echo, a tag would report success and change nothing.
      if (!(d0.symbols || []).includes(want)) throw new Error('tag not stored');
      if (wantTf && !(d0.timeframes || []).includes(wantTf)) throw new Error('TF tag not stored');
      delete pineVerCache[pineId];
      const d = await fetch(`${API_BASE_URL}/api/bot/versions/${encodeURIComponent(pineId)}`).then(x => x.json());
      pineVerCache[pineId] = Array.isArray(d?.versions) ? d.versions : [];
      pineVerCacheTs[pineId] = Date.now();
      setSsotNonce(n => n + 1);   // RULE #14: re-ask the engine which version now runs
      setVers(pineVerCache[pineId]); setSel(''); setBusy('done');
      setTimeout(() => setBusy(''), 2500);
    } catch (e: any) {
      setErr(String(e?.message || 'failed').slice(0, 40));
      setBusy('err'); setTimeout(() => { setBusy(''); setErr(''); }, 4000);
    }
  };
  if (!pineId) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...(grow ? { flex: 1, minWidth: 0 } : {}) }}>
      <select value={sel}
        onChange={e => {
          const val = e.target.value;
          if (val === '__showall__') { setShowAll(true); setSel(''); return; }
          if (val === '__hideall__') { setShowAll(false); setSel(''); return; }
          setSel(val);
        }}
        title={(engineV != null
            ? `⚡ ENGINE (SSOT /scanner/pine-active): v${engineV} · ${ssot?.source || ''} · kind ${ssot?.kind || '?'} — YAHI is leg par chal raha hai.\n`
            : '')
          + (vMismatch ? `🔴 UI computation v${localV} se ALAG — engine ka number hi sach hai.\n` : '')
          + (mine.length
            ? `${symbol}${tfSfx}: LATEST = v${mine[0].version_num} (${dt(mine[0])}) — wahi router chalata hai`
            + (tfTagged.length ? ` [is TF ka apna Pine]`
              : wantTf ? ` [symbol-level Pine — is TF ka apna nahi; tag karoge to sirf ${wantTf} leg badlega]` : '')
            + `; purane ${mine.length - 1} version(s) history me saved hain`
            : `${symbol}${tfSfx} ka koi tagged Pine nahi — list se koi version chunke DEPLOY dabao`)}
        style={{ maxWidth: grow ? undefined : 168, ...(grow ? { flex: 1, minWidth: 0 } : {}),
          background: vMismatch ? 'rgba(239,68,68,0.12)' : mine.length ? 'rgba(16,185,129,0.10)' : '#0d1224',
          border: `1px solid ${vMismatch ? 'rgba(239,68,68,0.6)' : mine.length ? 'rgba(16,185,129,0.45)' : '#334155'}`, borderRadius: 5,
          color: vMismatch ? '#f87171' : mine.length ? '#34d399' : '#64748b', fontSize: 9.5, fontWeight: 800, padding: '3px 3px',
          outline: 'none', cursor: 'pointer' }}>
        <option value="">
          {engineV != null
            // ⚡ ENGINE = SSOT resolver ka jawab (wahi jo bot chalata hai)
            ? `${vMismatch ? '⚠' : '⚡'} ${want}${tfSfx} · ENGINE v${engineV}${vMismatch ? ` (UI v${localV}!)` : ''} · ${String(ssot?.kind || '').toUpperCase()}`
            : mine.length
              // ✅ = is leg ka apna Pine · ↳ = symbol-level Pine inherit ho raha hai
              ? `${inherited ? '↳' : '✅'} ${want}${tfSfx} · LATEST v${mine[0].version_num} · ${dt(mine[0])}`
              : `📜 no ${want}${tfSfx} pine`}
        </option>
        {mine.length > 0 && (
          <optgroup label={inherited
            ? `↳ ${want} SYMBOL-LEVEL PINE (${wantTf} leg isi ko inherit karta hai)`
            : `✅ ${want}${tfSfx} VERSION HISTORY (latest sabse upar)`}>
            {mine.map(v => (
              <option key={String(v._id)} value={String(v._id)}>
                {String(v._id) === activeId ? '✅ LATEST · ' : '🕘 old · '}{label(v)}
              </option>
            ))}
          </optgroup>
        )}
        {(showAll || mine.length === 0) && rest.length > 0 && (
          <optgroup label={`UNTAGGED / other ${wantTf ? 'symbols+TF' : 'symbols'} — chunke DEPLOY = ${want}${tfSfx} par tag`}>
            {rest.map(v => (
              <option key={String(v._id)} value={String(v._id)}>
                {pvSymsOf(v).length ? `[${pvSymsOf(v).join(',')}] ` : '[generic] '}{label(v)}
              </option>
            ))}
          </optgroup>
        )}
        {mine.length > 0 && rest.length > 0 && (
          <option value={showAll ? '__hideall__' : '__showall__'}>
            {showAll ? '▲ dusre codes chhupao (clean view)' : `➕ naya code tag karna hai? (${rest.length} aur)`}
          </option>
        )}
      </select>
      {sel && onLoad && byId(sel) && (
        <button onClick={() => { const v = byId(sel); if (v) onLoad(codeOf(v), v); }}
          title="Ye version editor me LOAD karo (koi tag/deploy nahi hota)"
          style={{ background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.5)',
            color: '#38bdf8', borderRadius: 5, fontSize: 9, fontWeight: 900, padding: '3px 7px', cursor: 'pointer' }}>
          ⬇ LOAD
        </button>
      )}
      {sel && sel !== activeId && (
        <button onClick={deploy} disabled={busy === 'saving'}
          title={wantTf
            ? `Ye version ${want}@${wantTf} ka naya LATEST bana do — SIRF ${wantTf} leg badlega, ${want} ke dusre TF legs par koi asar nahi`
            : `Ye version ${want} ka naya LATEST bana do (router isi ko chalayega)`}
          style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.5)',
            color: '#34d399', borderRadius: 5, fontSize: 9, fontWeight: 900, padding: '3px 7px', cursor: 'pointer' }}>
          {busy === 'saving' ? '…' : busy === 'err' ? '✗ retry' : '🚀 DEPLOY'}
        </button>
      )}
      {busy === 'done' && <span style={{ color: '#34d399', fontSize: 9, fontWeight: 800 }}>✓ tagged</span>}
      {busy === 'err' && err && <span style={{ color: '#f87171', fontSize: 9, fontWeight: 800 }}>✗ {err}</span>}
      {/* 👁 ACTIVE CODE — exactly the Pine this symbol's pane/engine will run,
          with its source (tagged vs generic fallback) spelled out. */}
      {activeV && (
        <button onClick={() => setShowCode(true)}
          title={`${symbol}${tfSfx} par ACTIVE Pine dekho — v${activeV.version_num} (${activeSrc})`}
          style={{ background: mine[0] ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
            border: `1px solid ${mine[0] ? 'rgba(16,185,129,0.45)' : 'rgba(245,158,11,0.45)'}`,
            color: mine[0] ? '#34d399' : '#fbbf24', borderRadius: 5, fontSize: 9, fontWeight: 900,
            padding: '3px 6px', cursor: 'pointer' }}>
          👁 v{activeV.version_num}{tfTagged.length ? tfSfx : ''}
        </button>
      )}
      {showCode && activeV && (
        <div onClick={() => setShowCode(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(2,6,23,0.72)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 'min(760px, 92vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              background: '#0b1020', border: '1px solid #334155', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
              borderBottom: '1px solid #1e293b' }}>
              <span style={{ fontSize: 11, fontWeight: 900, color: mine[0] ? '#34d399' : '#fbbf24' }}>
                📜 {want}{tfSfx} ka ACTIVE PINE — v{activeV.version_num} · {activeSrc} · {String(activeV.createdAt || '').slice(0, 10)} · {activeV.status || 'DRAFT'}
              </span>
              {!mine[0] && (
                <span style={{ fontSize: 9, fontWeight: 800, color: '#fbbf24' }}>
                  ⚠ is symbol ka apna tagged Pine NahI — generic chalega
                </span>
              )}
              {mine[0] && wantTf && !tfTagged.length && (
                <span style={{ fontSize: 9, fontWeight: 800, color: '#fbbf24' }}>
                  ↳ {wantTf} ka apna Pine nahi — symbol-level Pine inherit ho raha hai
                </span>
              )}
              {onLoad && (
                <button onClick={() => { onLoad(codeOf(activeV), activeV); setShowCode(false); }}
                  style={{ background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.5)',
                    color: '#38bdf8', borderRadius: 5, fontSize: 9, fontWeight: 900, padding: '3px 8px', cursor: 'pointer' }}>
                  ⬇ LOAD
                </button>
              )}
              <button onClick={() => setShowCode(false)} style={{ marginLeft: 'auto', background: 'none',
                border: 'none', color: '#64748b', fontSize: 15, cursor: 'pointer' }}>✕</button>
            </div>
            <pre style={{ margin: 0, padding: '10px 12px', overflow: 'auto', fontSize: 10.5,
              lineHeight: 1.45, color: '#cbd5e1', fontFamily: 'ui-monospace, Consolas, monospace' }}>
              {String(activeV.pine_code || activeV.code_json || '(is version me code khaali hai)')}
            </pre>
          </div>
        </div>
      )}
    </span>
  );
}
